<script setup lang="ts">
import type { Addons, SimulationConclusion } from '~/utils/simulation/Simulation'
import { useToast } from '@solfacil/girassol'
import { useTrackFinancingDetailsExited } from '~/composables/analytics/useTrackFinancingDetailsExited'
import { useTrackProposalStarted } from '~/composables/analytics/useTrackProposalStarted'
import { useInsuranceStorage } from '~/composables/useInsuranceStorage'
import { FEATURE_FLAGS } from '~/constants'
import queryClient, { queryKeys } from '~/queryClient'
import SimulatorService from '~/services/simulator/Simulator'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import useLegacyFinancing from '~/store-v2/legacy-financing/useLegacyFinancing'

const props = defineProps<{
  disabledFields: boolean
  loadingConclusion: boolean
  isOnflexPartner: boolean
  dueDay?: number | null
  firstDueDate?: string | null
}>()
const { createErrorToast, createSuccessToast } = useToast()
const { getSelectedInstallment, getValueOfAmpera, getAddonsApplieds, getCurrentProject, getCommissionApplied, getGracePeriodApplied } = useResumeStore()
const { clearInsuranceDecision } = useInsuranceStorage()

const { t } = useI18n()
const router = useRouter()
const route = useRoute<any>()
const { trackFinancingDetailsExited } = useTrackFinancingDetailsExited({ projectId: route.params.id as string })
const { trackProposalStarted } = useTrackProposalStarted({ projectId: route.params.id as string })
const simulatorService = new SimulatorService()

const { data: enableDynamicDueDayData } = useFeatureFlag<boolean>(FEATURE_FLAGS.ENABLE_DYNAMIC_DUE_DAY, false, true)

const blockFields = ref(false)
const loadingLocal = ref(false)

const disabledButton = ref(true)

const config = import.meta.env

onUpdated(() => {
  disabledButton.value = !props.disabledFields || blockFields.value || !getSelectedInstallment() || loadingLocal.value
})

const currentProject = getCurrentProject()

const { data: legacyFinancing, isLoading: isLoadingLegacyFinancing } = useLegacyFinancing(route.params.id as string, {
  skip: !currentProject?.complete,
})

async function submitFinancial() {
  try {
    loadingLocal.value = true
    const selectedInstallment = getSelectedInstallment()
    const commission = getCommissionApplied()

    if (selectedInstallment === null || currentProject === null || commission === undefined)
      return

    const addonsAppliedId = getAddonsApplieds().map((addon: Addons) => {
      if (addon.applied)
        return addon.id

      return null
    }).filter((n: string | null) => n)

    if (!selectedInstallment.combo) {
      addonsAppliedId.push(selectedInstallment.simulation_id)
    }

    const payload: SimulationConclusion = {
      id: selectedInstallment.engine_simulation_id,
      new_journey: true,
      is_combo: selectedInstallment.combo,
      is_ampera: Boolean(getValueOfAmpera()),
      commission: selectedInstallment?.is_from_suggested ? Number(selectedInstallment.commission) : Number(commission),
      simulation_id: selectedInstallment.simulation_id,
      project_id: currentProject.id,
      applied_addons: addonsAppliedId as any[],
      is_from_suggested: selectedInstallment?.is_from_suggested,
    }
    const response = await simulatorService.create_financial(payload)

    if (currentProject.id) {
      clearInsuranceDecision(currentProject.id)
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.FINANCING.DETAILS(currentProject.id) })

    createSuccessToast('Financiamento criado com sucesso.')
    trackFinancingDetailsExited({
      projectId: currentProject.id,
      personType: currentProject.person_type,
      enable_dynamic_due_day: enableDynamicDueDayData.value ?? false,
    })
    trackProposalStarted({
      projectId: currentProject.id,
      personType: currentProject.person_type,
    })

    if (response.data.new_journey) {
      if (currentProject.person_type === 'PF')
        return router.push(`/customer/${currentProject.id}`)

      return router.push(`/company/${currentProject.id}`)
    }
  }
  catch (e: any) {
    if (e.response?.status === 422)
      createErrorToast('Selecione ao menos uma parcela.')
    else
      createErrorToast('Ocorreu um erro inesperado.')
  }
  finally {
    loadingLocal.value = false
  }
}

function redirectFormalization() {
  if (legacyFinancing.value && !currentProject?.new_journey) {
    window.open(`${config.VITE_URL_LEGACY}/financiamento/etapas/${legacyFinancing.value.financingId}`, '_self')
    return
  }

  if (currentProject?.person_type === 'PF')
    return router.push(`/customer/${currentProject?.id}`)

  return router.push(`/company/${currentProject?.id}`)
}
</script>

<template>
  <div class="w-full justify-end">
    <div class="w-full flex gap-3 justify-end">
      <SimulationElementDownloadList
        v-if="!currentProject?.complete"
        :commission="Number(getCommissionApplied())"
        :grace-period="getGracePeriodApplied() as number"
        :is-combo="getSelectedInstallment()?.combo"
        :due-day="props.dueDay"
        :first-due-date="props.firstDueDate"
      />
      <SolButton
        v-if="currentProject?.complete"
        id="customer"
        size="large"
        :loading="isLoadingLegacyFinancing"
        @click="redirectFormalization"
      >
        {{ t('simulation.return_formalization') }}
      </SolButton>
      <SolButton
        v-if="!currentProject?.complete"
        id="go"
        :disabled="loadingConclusion"
        :loading="loadingLocal"
        size="large"
        @click="submitFinancial"
      >
        {{ t('simulation.conclude') }}
      </SolButton>
    </div>
  </div>
</template>
